import { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router'
import { api, useApi, type PodcastRow, type Rec } from '../api'
import { useIsMobile } from '../layout'
import { useMetric } from '../metric'
import { usePageTitle } from '../title'
import { RecCardDesktop, RecCardMobile } from '../components/RecCard'
import { Empty, ErrorState, Icon, Loading, MetricSwitch } from '../components/primitives'

const PAGE_SIZE = 50
const TITLE = 'Recent recommendations'
const SUB = 'Every stock pick extracted from leading business podcasts, tracked from the moment it aired.'

function feedPath(podcastId: string | null, page: number): string {
  const qs = new URLSearchParams()
  if (podcastId) qs.set('podcastId', podcastId)
  if (page > 1) qs.set('page', String(page))
  const s = qs.toString()
  return `/api/recommendations${s ? `?${s}` : ''}`
}

// One page of the feed; the last page decides whether "Load more" shows.
function FeedPage({ podcastId, page, mobile, onLoaded }: {
  podcastId: string | null
  page: number
  mobile: boolean
  onLoaded?: (count: number) => void
}) {
  const { data, error, retry } = useApi<Rec[]>(feedPath(podcastId, page))
  const count = data?.length
  useEffect(() => {
    if (count !== undefined) onLoaded?.(count)
  }, [count, onLoaded])
  if (error) return <ErrorState error={error} retry={retry} />
  if (!data) return page === 1 ? <Loading /> : null
  if (page === 1 && data.length === 0) return <Empty>No picks yet{podcastId ? ' from this podcast' : ''}.</Empty>
  const Card = mobile ? RecCardMobile : RecCardDesktop
  return <>{data.map((r) => <Card key={r.id} r={r} />)}</>
}

function Feed({ podcastId, mobile }: { podcastId: string | null; mobile: boolean }) {
  const [pages, setPages] = useState(1)
  const [lastCount, setLastCount] = useState<number | null>(null)
  const onLastLoaded = useCallback((n: number) => setLastCount(n), [])
  return (
    <>
      <div className={mobile ? 'm-feed' : 'rec-grid'} data-testid="feed">
        {Array.from({ length: pages }, (_, i) => (
          <FeedPage
            key={i}
            podcastId={podcastId}
            page={i + 1}
            mobile={mobile}
            onLoaded={i + 1 === pages ? onLastLoaded : undefined}
          />
        ))}
      </div>
      {lastCount === PAGE_SIZE && (
        <button className={mobile ? 'm-btn' : 'btn'} style={{ marginTop: 14 }} onClick={() => { setLastCount(null); setPages((p) => p + 1) }}>
          Load more
        </button>
      )}
    </>
  )
}

export function Home() {
  usePageTitle(undefined)
  const mobile = useIsMobile()
  const { metric, setMetric } = useMetric()
  const [params, setParams] = useSearchParams()
  const podcastId = params.get('podcast')
  const podcasts = useApi<PodcastRow[]>(api.podcasts()).data?.filter((p) => p.pick_count > 0) ?? []

  const choose = (id: string | null) =>
    setParams(id ? { podcast: id } : {}, { replace: true, preventScrollReset: true })

  if (mobile) {
    return (
      <div>
        <div className="row ai-fs jc-sb g-10">
          <h1 className="m-h1">{TITLE}</h1>
          <div style={{ marginTop: 10, flexShrink: 0 }}><MetricSwitch metric={metric} onChange={setMetric} /></div>
        </div>
        <p className="m-sub">{SUB}</p>
        <div className="m-chips" role="group" aria-label="Filter by podcast">
          <button className={`m-chip ${!podcastId ? 'active' : ''}`} onClick={() => choose(null)} aria-pressed={!podcastId}>All podcasts</button>
          {podcasts.map((p) => (
            <button key={p.id} className={`m-chip ${podcastId === p.id ? 'active' : ''}`} onClick={() => choose(p.id)} aria-pressed={podcastId === p.id}>
              {p.name}
            </button>
          ))}
        </div>
        <Feed key={podcastId ?? 'all'} podcastId={podcastId} mobile />
      </div>
    )
  }

  return (
    <div>
      <h1 className="page-title">{TITLE}</h1>
      <p className="page-sub">{SUB}</p>
      <div className="filter-row" role="group" aria-label="Filter by podcast">
        <button className={`chip ${!podcastId ? 'active' : ''}`} onClick={() => choose(null)} aria-pressed={!podcastId}>All podcasts</button>
        {podcasts.map((p) => (
          <button key={p.id} className={`chip ${podcastId === p.id ? 'active' : ''}`} onClick={() => choose(p.id)} aria-pressed={podcastId === p.id}>
            {p.name}
          </button>
        ))}
        <span className="sep" />
        <span className="sort">Most recent <Icon name="chevron" size={12} /></span>
        <MetricSwitch metric={metric} onChange={setMetric} />
      </div>
      <Feed key={podcastId ?? 'all'} podcastId={podcastId} mobile={false} />
    </div>
  )
}
